import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { logAudit } from '../../lib/audit'

interface Props {
  requestedAt: string
  /** Callback após cancelar — typically refresh do useOnboarding */
  onCancel?: () => void | Promise<void>
}

export default function DeletedAccountPage({ requestedAt, onCancel }: Props) {
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requested = new Date(requestedAt)
  const purgeAt = new Date(requested.getTime() + 30 * 24 * 60 * 60 * 1000)
  const daysLeft = Math.max(0, Math.ceil((purgeAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))

  async function handleCancel() {
    setError(null)
    setWorking(true)
    const { error } = await supabase.rpc('cancel_account_deletion')
    setWorking(false)
    if (error) {
      setError(error.message)
      return
    }
    void logAudit('account.delete_cancel', { target_type: 'profile' })
    if (onCancel) await onCancel()
  }

  async function handleSignOut() {
    setWorking(true)
    await supabase.auth.signOut()
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-surface border border-warn/40 rounded-xl p-8 text-center">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="text-xl font-bold text-ink mb-2">Conta marcada para exclusão</h1>
        <p className="text-sm text-ink-muted mb-4">
          Você solicitou a exclusão desta conta em{' '}
          <strong className="text-ink">{requested.toLocaleString('pt-BR')}</strong>.
        </p>
        <p className="text-sm text-ink-muted mb-6">
          Os dados serão eliminados em <strong className="text-[#9a5a28] dark:text-amber-300">{daysLeft} dia{daysLeft !== 1 ? 's' : ''}</strong>
          {' '}(até {purgeAt.toLocaleDateString('pt-BR')}). Você pode cancelar este pedido a qualquer momento dentro
          desse prazo.
        </p>

        {error && (
          <div className="mb-4 text-xs text-danger bg-danger/10 border border-danger/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <button
            onClick={handleCancel}
            disabled={working}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
          >
            {working ? 'Cancelando...' : 'Cancelar exclusão e voltar a usar'}
          </button>
          <button
            onClick={handleSignOut}
            disabled={working}
            className="w-full bg-sunken hover:bg-line disabled:opacity-40 text-ink-muted font-medium py-2.5 rounded-lg text-sm transition-colors"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  )
}
